import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { CreatorPicker } from "@/components/CreatorPicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { TagsInput } from "@/components/TagsInput"
import { usePurposes } from "@/lib/queries/purposes"
import { useGetVideo, useUpdateVideo } from "@/lib/queries/videos"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Edit Video
// LEGACY: apps/admin/src/pages/UpdateVideo.jsx
// API:    POST /admin/get_video    { id }
//         POST /admin/update_video { id, purpose, purpose_id, author,
//                                    narrater, tags: string[], proverb,
//                                    login_user_sequence, skip_user_sequence }
//
// Field parity (legacy ↔ v2):
//   purpose             ← Purpose dropdown LABEL
//   purpose_id          ← Purpose dropdown ObjectId
//   author              ← Author Name input
//   narrater            ← Narrater Name input  (typo PRESERVED)
//   tags                ← Tags input  (string[] — built from `keyword` on load)
//   proverb             ← Proverb textarea
//   login_user_sequence ← number input (1..total videos)
//   skip_user_sequence  ← number input
//
// Note: edit form does NOT touch the file, S3 data, thumbnails, or the
// notification toggle. Those are upload-time concerns. Same as legacy.
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  purpose_id: z.string().min(1, "Pick a purpose"),
  author: z.string().optional(),
  narrater: z.string().optional(),
  proverb: z.string().optional(),
  tags: z.array(z.string()),
  login_user_sequence: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().int().min(0).optional()
  ),
  skip_user_sequence: z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number().int().min(0).optional()
  ),
})

type FormValues = z.infer<typeof schema>

export default function EditVideo() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const purposesQ = usePurposes()
  const videoQ = useGetVideo(id)
  const updateMut = useUpdateVideo()

  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      purpose_id: "",
      author: "",
      narrater: "",
      proverb: "",
      tags: [],
      login_user_sequence: undefined,
      skip_user_sequence: undefined,
    },
  })

  // Hydrate form from the fetched video. Done in an effect (not via
  // defaultValues) so we can react to videoQ resolving asynchronously.
  useEffect(() => {
    if (!videoQ.data) return
    const v = videoQ.data
    const tagsFromKeyword = Array.isArray(v.keyword)
      ? v.keyword.filter((t): t is string => typeof t === "string")
      : []
    reset({
      purpose_id: v.purpose_id ?? "",
      author: v.author ?? "",
      narrater: v.narrated_by ?? "",
      proverb: v.proverb ?? "",
      tags: tagsFromKeyword,
      login_user_sequence: v.login_user_sequence,
      skip_user_sequence: v.skip_user_sequence,
    })
  }, [videoQ.data, reset])

  const onSubmit = handleSubmit(async (values) => {
    if (!id) return
    setSubmitError(null)
    const purposeOpt = purposesQ.data?.find((p) => p._id === values.purpose_id)
    const purposeLabel = purposeOpt?.purpose ?? ""

    try {
      const res = await updateMut.mutateAsync({
        id,
        purpose: purposeLabel,
        purpose_id: values.purpose_id,
        author: values.author ?? "",
        narrater: values.narrater ?? "",
        tags: values.tags,
        proverb: values.proverb ?? "",
        login_user_sequence: values.login_user_sequence ?? "",
        skip_user_sequence: values.skip_user_sequence ?? "",
      })
      if (!res?.success) {
        setSubmitError(res?.message || "Failed to update video.")
        return
      }
      navigate("/videos")
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to update video.")
    }
  })

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/videos">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to videos
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit video</CardTitle>
          <CardDescription>
            {videoQ.isLoading
              ? "Loading…"
              : videoQ.data?.narrated_by || videoQ.data?.author || "Video"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {videoQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load this video. Try refreshing.</span>
            </div>
          )}

          {videoQ.isLoading ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              {/* Purpose */}
              <div className="space-y-2">
                <Label htmlFor="purpose">Purpose</Label>
                <Controller
                  control={control}
                  name="purpose_id"
                  render={({ field }) => (
                    <Select
                      value={field.value || undefined}
                      onValueChange={field.onChange}
                      disabled={purposesQ.isLoading}
                    >
                      <SelectTrigger id="purpose" aria-invalid={!!errors.purpose_id}>
                        <SelectValue placeholder="Choose a purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        {purposesQ.data?.map((p) => (
                          <SelectItem key={p._id} value={p._id}>
                            {p.purpose}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.purpose_id && (
                  <p className="text-xs text-destructive">{errors.purpose_id.message}</p>
                )}
              </div>

              {/* Author — picker against the Creators directory */}
              <div className="space-y-2">
                <Label htmlFor="author">Author</Label>
                <Controller
                  control={control}
                  name="author"
                  render={({ field }) => (
                    <CreatorPicker
                      id="author"
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder="Pick or create the author"
                    />
                  )}
                />
              </div>

              {/* Narrater (legacy field name preserved) — same picker */}
              <div className="space-y-2">
                <Label htmlFor="narrater">Narrater</Label>
                <Controller
                  control={control}
                  name="narrater"
                  render={({ field }) => (
                    <CreatorPicker
                      id="narrater"
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder="Pick or create the narrator"
                    />
                  )}
                />
              </div>

              {/* Proverb */}
              <div className="space-y-2">
                <Label htmlFor="proverb">Proverb</Label>
                <Textarea id="proverb" rows={4} {...register("proverb")} />
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label>Tags</Label>
                <Controller
                  control={control}
                  name="tags"
                  render={({ field }) => (
                    <TagsInput value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="login_user_sequence">Login user sequence</Label>
                  <Input
                    id="login_user_sequence"
                    type="number"
                    min={0}
                    {...register("login_user_sequence")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="skip_user_sequence">Skip user sequence</Label>
                  <Input
                    id="skip_user_sequence"
                    type="number"
                    min={0}
                    {...register("skip_user_sequence")}
                  />
                </div>
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={updateMut.isPending}>
                  {updateMut.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Update video
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/videos")}
                  disabled={updateMut.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
